import Vue from "vue";
import Component from 'vue-class-component';
import { Esri } from '@/esriMap';
import { IModalBase, DataItem, modalHelper } from '@/components';
import { Deferred } from '@/utils/Deferred';
import { Prop } from 'vue-property-decorator';
import { arcgisToGeoJSON, geojsonToArcGIS } from "@esri/arcgis-to-geojson-utils";
import { areaOmogeneaStore } from '@/modules/AreeOmogenee/store/areaOmogenera.store';
import { Async } from '@/utils/AsyncDecorators';
import { parentSymbol, symbol, invalidSymbol } from '../module';
import { areaBoscoDaSemeStore } from '@/modules/AreeBoscoDaSeme/store/areaBoscoDaSeme.store';
import { addFeatureLayers, addMapImageLayer, addExistingGraphics } from '@/utils/mapFeatureLayer';
import { areaRaccoltaStore } from '../store/areaRaccolta.store';
import { toastHelper } from '@/helpers/ToastHelper';
import { geometry } from '@turf/turf';
import { layoutHelper } from '@/helpers/LayoutHelper';

@Component({})
export default class Map extends Vue implements IModalBase<server.areaRaccolta>{
  @Prop()
  value: server.areaRaccolta;

  @Prop()
  deferred: Deferred<DataItem<server.areaRaccolta>>;

  map: __esri.Map = null;
  mapview: __esri.MapView = null;

  sketchLayer: __esri.GraphicsLayer = null;

  isValidGeometry: boolean = false;
  currentGraphic: __esri.Graphic;

  parentGraphic: __esri.Graphic = null;
  parentLayer: __esri.GraphicsLayer = null;
  exsitingGraphics: __esri.Graphic[] = [];


  async mounted() {

    let parent = await areaBoscoDaSemeStore.getters.get(this.value.areaBoscoDaSemeId);

    this.map = await Esri.Map({ basemap: 'hybrid' } as __esri.MapProperties);
    this.mapview = await Esri.Views.MapView({
      map: this.map,
      zoom: 12,
      center: [12, 43],
      container: "map",
      spatialReference: {
        wkid: 102100
      },
      popup: {
        defaultPopupTemplateEnabled: true,
        collapseEnabled: true,
        collapsed: false,
      }
    } as __esri.MapViewProperties);


    this.parentLayer = await Esri.Layers.GraphicsLayer({
      listMode: 'hide'
    });
    this.sketchLayer = await Esri.Layers.GraphicsLayer({
      listMode: 'hide'
    });
    this.map.layers.add(this.parentLayer, 1);
    this.map.layers.add(this.sketchLayer, 2);


    if (parent && parent.geoJSON)
      this.parentGraphic = await this.addGraphic(parent.geoJSON, parentSymbol, this.parentLayer, false);

    if (this.value.geoJSON) {
      this.currentGraphic = await this.addGraphic(this.value.geoJSON, symbol, this.sketchLayer, true)
    }

    let sketch = await Esri.Widgets.Sketch({
      view: this.mapview,
      layer: this.sketchLayer,
      updateOnGraphicClick: true,
      creationMode: 'update',
      availableCreateTools: ['polygon']
    } as __esri.SketchProperties);

    this.mapview.ui.add(sketch, "top-right");

    await this.addTools();

    await addMapImageLayer(this.map);
    await addFeatureLayers(this.map);

    const aree = (await areaRaccoltaStore.getters.getAll()).filter(f => f.id !== this.value.id && f.areaBoscoDaSemeId === this.value.areaBoscoDaSemeId);
    this.exsitingGraphics = await addExistingGraphics(this.map, "polygon", "Aree raccolta presenti", aree);


    let $this = this;

    sketch.on('create', (evt) => {
      if (evt.state === 'complete') {
        $this.checkGraphic(evt.graphic, true);
      }
    });

    sketch.on(["update"], (evt) => {
      if (evt.state === "complete") {
        $this.isValidGeometry = false;
      } else {
        if (evt.toolEventInfo) {
          const toolType = evt.toolEventInfo.type;
          $this.checkGraphic(evt.graphics[0], (toolType === "move-stop" || toolType === "reshape-stop" || toolType === "transform-stop"));
        }
      }
    })

    this.mapview.on("click", function (event) {

      if (sketch.state === "active") {
        return;
      }
      $this.mapview.hitTest(event).then(function (response) {
        var results = response.results;


        results.forEach(function (result) {
          if (
            result.graphic.layer === sketch.layer &&
            result.graphic.attributes &&
            result.graphic.attributes.newDevelopment
          ) {
            sketch.update([result.graphic], { tool: "reshape" });
          }
        });
      });
    });

  }

  async addTools() {
    var searchWidget = await Esri.Widgets.Search({ view: this.mapview })
    var expand = await Esri.Widgets.Expand({ id: "search", expandIconClass: "esri-icon-search", expandTooltip: "Trova indirizzo o luogo", view: this.mapview, content: searchWidget });
    this.mapview.ui.add(expand, "top-left");

    Esri.WidgetsFactory.BasemapGallery(this.mapview, "top-left");
    Esri.WidgetsFactory.LayerList(this.mapview, "top-left");

    const map_buttons = document.getElementById("map-buttons");
    this.mapview.ui.add(map_buttons);

    const sampleInstructions = document.getElementById("instruction-widget");
    let instructionsExpand = await Esri.Widgets.Expand({
      id: "question",
      expandIconClass: "esri-icon-question",
      expandTooltip: "Come catturare la geometria",
      expanded: layoutHelper.isWeb,
      view: this.mapview,
      content: sampleInstructions
    });
    this.mapview.ui.add(instructionsExpand, "top-left");
  }

  async checkGraphic(graphic: any, showMessage: boolean = false) {
    let geoEngine = await Esri.Geometry.geometryEngine();
    this.isValidGeometry = false;
    let geometry = graphic.geometry;

    const checkNotContains = this.parentGraphic && !geoEngine.contains(this.parentGraphic.geometry, geometry);
    const checkIntersect = this.exsitingGraphics.length > 0 && (geoEngine.intersect(this.exsitingGraphics.map(m => m.geometry), geometry) as __esri.Geometry[]).filter(f => f != null).length > 0;

    if (checkNotContains) {
      graphic.symbol = invalidSymbol;
      if (showMessage) toastHelper.error("L'area di raccolta deve essere contenuta<br>nell'area bosco da seme selezionata");
    } else {
      if (checkIntersect) {
        graphic.symbol = invalidSymbol;
        if (showMessage) toastHelper.error("L'area di raccolta non si può sovrapporre<br>ad altre aree di raccolta inserite");
      } else {
        graphic.symbol = symbol
        this.currentGraphic = graphic;
        this.isValidGeometry = true;
      }
    }
  }

  async addGraphic(geojson: __esri.Geometry, _symbol: any, layer: __esri.GraphicsLayer, isPrincipal: boolean) {
    let polygonConstructor: __esri.PolygonConstructor = await Esri.Geometry.PolygonConstructor();
    let geometry = new polygonConstructor(geojsonToArcGIS(geojson));

    if (geometry && geometry.spatialReference.isWGS84)
      geometry = (await Esri.Geometry.Support.webMercatorUtils()).geographicToWebMercator(geometry) as any;

    let graphic = await Esri.Graphic({
      geometry: geometry,
      symbol: _symbol
    });

    if (isPrincipal) {
      this.checkGraphic(graphic, true);
      await this.mapview.goTo(geometry)
    }

    if (layer) layer.graphics.add(graphic)
    else this.mapview.graphics.add(graphic)

    await this.mapview.goTo(geometry)

    return graphic;
  }

  close() {
    modalHelper.Unchanged(this);
    modalHelper.closeModal("extended");
  }

  async saveGeometry() {
    let geoEngine = await Esri.Geometry.geometryEngine();
    let geographicGeometry = (await Esri.Geometry.Support.webMercatorUtils()).webMercatorToGeographic(this.currentGraphic.geometry);
    this.value.area = Math.abs(geoEngine.geodesicArea(this.currentGraphic.geometry as __esri.Polygon, "square-meters"));
    this.value.area = parseInt(this.value.area.toFixed(0));
    this.value.geoJSON = arcgisToGeoJSON(geographicGeometry);
    modalHelper.Changed(this);
  }
}